
import { toTranslationKey } from '@/utils/stringFormat';
import { maintenanceCategoriesKeys } from '@/consts/MaintenanceCategoriesKeys';
import { MaintenanceMixin } from '@/mixins/Maintenance';
import Loader from "@/components/loader/Loader.vue";
import MaintenanceRequestCancelDlg from "@/views/maintenance/maintenanceRequestCancelDlg/maintenanceRequestCancelDlg.vue";
import {MaintenanceRequestStatus} from "@/enums/MaintenanceRequestStatus";
import {ValidationObserver, ValidationProvider} from "vee-validate";
import {MaintenancePriority} from "@/enums/MaintenancePriority";
import {MaintenanceMediaTypes} from "@/enums/MaintenanceMediaTypes";
import {dateFormats} from "@/consts/DateTimeFormats";
import moment from "moment";
import MaintenanceRequestEditDlg from "@/views/maintenance/maintenanceRequestEditDlg/MaintenanceRequestEditDlg.vue";
import {MaintenanceFormStatus} from "@/enums/MaintenanceFormStatus";
import { StaffAccessLevels } from '@/enums/StaffAccessLevels';
import MaintenanceChecklistDlg from '@/views/maintenance/maintenanceChecklistDlg/MaintenanceChecklistDlg.vue';
import ConfirmDlg from "@/components/popup/confirm-dialog/ConfirmDlg.vue";
import ScheduledMaintenanceFormEditDlg
  from '@/views/maintenance/scheduledMaintenance/scheduledMaintenanceFormEditDlg/scheduledMaintenanceFormEditDlg.vue';
import ScheduledMaintenanceFormCompleteDlg
  from '@/views/maintenance/scheduledMaintenance/scheduledMaintenanceFormCompleteDlg/scheduledMaintenanceFormCompleteDlg.vue';
import MaintenanceUploadMedia from "@/views/maintenance/maintenanceUploadMedia/MaintenanceUploadMedia.vue";
import { hasAccess } from '@/utils/accessControl/accessControl';
import { AccessMatrixKeys } from '@/utils/accessControl/accessMatrixKeysEnum';

export default {
  name: 'MaintenanceView',
  components: {
    Loader,
    MaintenanceRequestCancelDlg,
    ValidationObserver,
    ValidationProvider,
    MaintenanceRequestEditDlg,
    MaintenanceChecklistDlg,
    ConfirmDlg,
    ScheduledMaintenanceFormEditDlg,
    ScheduledMaintenanceFormCompleteDlg,
    MaintenanceUploadMedia
  },
  mixins: [MaintenanceMixin],
  data() {
    return {
      loading: true,
      additionalResponse: '',
      selectedTechnician: null,
      ticketPriority: null,
      estimatedDeliveryDate: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().substr(0, 10),
      menu: false,
      isReassignRequest: false,
      headersForms: [
        {
          text: 'Form name',
          align: 'start',
          value: 'name',
        },
        {
          text: 'Location',
          align: 'start',
          value: 'locationId',
        },
        {
          text: 'Status',
          align: 'start',
          value: 'status',
        },
        {
          text: '',
          align: 'end',
          value: 'actions',
          sortable: false
        },
      ],
      jsonFields: {
        "Form": "title",
        "ID": "id"
      }
    };
  },
  computed: {
    MaintenanceRequestStatus() {
      return MaintenanceRequestStatus
    },
    request() {
      return this.$store.state.maintenanceModule.currentMaintenanceRequest;
    },
    createdBy() {
      let output = "";
      if (this.request?.createdByStaffName) output = this.request.createdByStaffName; 
      if (this.request?.createdByResidentName) output = this.request.createdByResidentName; 
      if (this.request?.scheduledMaintenanceId) output += " (Scheduled Maintenance)";
      if (this.request?.type === "QR Code") output = "QR Code";
      if (output === "") output = "-";
      return output; 
    },
    MaintenanceTechnicians() {
      return [...this.$store.state.userModule.maintenanceUsers, ...this.$store.state.userModule.maintenanceVendors.map(x => { return {...x, isVendor: true} })];
    },
    isNewRequest() {
      return this.request.status === this.MaintenanceRequestStatus.New;
    },
    ticketPriorityOptions() {
      return Object.values(MaintenancePriority);
    },
    facility() {
      return this.$store.state.facilityModule.selectedFacility;
    },
    estimatedDeliveryDateFormatted() {
      const dateFormat = dateFormats.find(x => x.format === this.facility.dateFormat);
      return moment(this.estimatedDeliveryDate).format(dateFormat ? dateFormat.name : dateFormats[0].name);
    },
    isAbleToEdit() {
      return [MaintenanceRequestStatus.New, MaintenanceRequestStatus.InProgress].includes(this.request?.status);
    },
    isCompleted() {
      return this.request.status === 'complete';
    },
    isCanceled() {
      return this.request.status === 'cancelled';
    },
    MaintenanceMediaTypes() {
      return MaintenanceMediaTypes;
    },
    RequestForms() {
      if (this.request.forms) {
        return this.request.forms.map(x => {
          const statusColor = x.status === MaintenanceFormStatus.Completed ? 'green--text' : 'orange--text';
          const isCompleted = x.status === MaintenanceFormStatus.Completed;

          return {
            ...x,
            statusColor: statusColor,
            isCompleted: isCompleted,
            actions: true
          }
        })
      }

      return [];
    },
    user() {
      return this.$store.state.authModule.user;
    },
    isUserHasPermission() {
      const listOfAccessLevels: string[] = [StaffAccessLevels.OrgAdmin, StaffAccessLevels.MaintenanceAdmin, StaffAccessLevels.MaintenanceUser, StaffAccessLevels.HomeAdmin];
      return listOfAccessLevels.includes(this.user.accessLevel) ?? false;
    },
    maintenanceFormsRequestJob() {
      const forms = this.$store.state.maintenanceModule.maintenanceFormsRequestJob;
      const sortOrder = this.request?.formsIds || [];

      const sortMap = Object.fromEntries(sortOrder.map((id, index) => [id, index]));

      return forms.slice().sort((a, b) => {
        return (sortMap[a.id] ?? Infinity) - (sortMap[b.id] ?? Infinity);
      });
    },
    extendJsonFields() {
      const result = {};
      let globalIndex = 1;

      for (const job of this.maintenanceFormsRequestJob) {
        const elements = JSON.parse(job.template)?.pages?.[0]?.elements || [];

        for (const el of elements) {
          const title = el.title ?? el.name;
          result[title] = globalIndex;
          globalIndex++;
        }
      }

      return {...this.jsonFields, ...result};
    },
    exportForms() {
      const forms = this.RequestForms;
      const array = [];

      if(forms && forms?.length) {
        for(let i = 0; i < forms?.length; i++) {
          const form = this.maintenanceFormsRequestJob.find(x => x.id === forms[i].formId);
          if(!form) return;
          const obj = {
            title: form?.name,
            id: form?.id,
          };
          const pages = JSON.parse(form.template).pages;

          if(form.result && pages) {
            const keys = [];
            pages.forEach((page) => {
              page.elements.map((element) => {
                keys.push({
                  title: element.title ?? element.name,
                  name: element.name
                });
              })
            });

            for (const key of keys) {
              const idx = this.extendJsonFields[key.title];
              const value = form.result[key.name];
              const template = JSON.parse(form.template);
              const checkboxes = template.pages[0].elements.find(x => x.name === key.name)?.choices;

              if(checkboxes) {
                if(Array.isArray(value)) {
                  obj[idx] = value.map(x => checkboxes.find(y => y.value === x)?.text ?? x);
                }

                if(typeof value === 'string') {
                  obj[idx] = checkboxes.find(x => x?.value === value)?.text ?? value;
                }
              } else {
                obj[idx] = this.getQuestionValue(value);
              }
            }
          }

          array.push(obj);
        }
      }

      return array;
    },
    canCompleteRequest() {
      return !!(!this.request?.forms || this.request?.forms?.length === 0 || this.request?.forms.every(x => x?.status === MaintenanceFormStatus.Completed));
    },
    canUploadMaintenenceMedia(){
        return hasAccess(this.user.accessLevel, AccessMatrixKeys.Maintenance_Requests_UploadMedia)
    }
  },
  async created() {
    await this.$store.dispatch('getMaintenanceRequest', this.$route.params.id);
    await this.$store.dispatch('getMaintenanceUsers');
    await this.$store.dispatch("getMaintenanceVendors");
    await this.$store.dispatch('getMaintenanceTypes');
    await this.$store.dispatch('getMaintenanceCategories');
    await this.$store.dispatch('getMaintenanceFormsByMaintenanceRequestId', this.$route.params.id);
    this.setupRequestData()
    
    this.loading = false;
  },
  methods: {
    setupRequestData(){
      this.selectedTechnician = this.request.assigneeEmail ?? null;
      this.ticketPriority = this.request.priority ?? null;
      this.estimatedDeliveryDate = this.request.estimatedCompletionDate ?? this.estimatedDeliveryDate;
    },
    convertToTranslationKey(text) {
      return toTranslationKey(text);
    },
    routeToRequestList() {
      if(!this.isNewRequest) {
        this.$router.push('/maintenanceRequests?tab=2');
      } else {
        this.$router.push('/maintenanceRequests');
      }
    },
    async completeRequest() {
      await this.$store.dispatch('completeMaintenanceRequest', {
        id: this.$route.params.id,
        additionalResponse: this.additionalResponse,
      });
    },
    async assignRequest() {
      await this.$store.dispatch('assignMaintenanceRequest', {
        id: this.$route.params.id,
        assignee: this.MaintenanceTechnicians.find(x => x.email === this.selectedTechnician)?.name ?? null,
        assigneeId: this.MaintenanceTechnicians.find(x => x.email === this.selectedTechnician)?.id ?? null,
        assigneeEmail: this.MaintenanceTechnicians.find(x => x.email === this.selectedTechnician)?.email ?? null,
        assigneePhoneNumber: this.MaintenanceTechnicians.find(x => x.email === this.selectedTechnician)?.phoneNumber ?? null,
        estimatedCompletionDate: this.estimatedDeliveryDate,
        priority: this.ticketPriority,
        isSendEmailToVendor: this.request.isSendEmailToVendor ?? null
      });
    },
    getColor(priority){
       if(priority == 'Urgent' || priority == 'High'){
        return 'red--text'
       }else if(priority == 'Medium'){
        return 'orange--text'
       }else{
        return 'green--text'
       }

    },
    checkExistCategory(category) {
      const key = toTranslationKey(category);
      return maintenanceCategoriesKeys.includes(key);
    },
    openScheduledMaintenance() {
      this.$router.push("/scheduledMaintenance/" + this.request.scheduledMaintenanceId)
    },
    cancelRequest() {
      this.$refs.maintenanceRequestCancelDlg.open(this.request.id)
    },
    getStatus(status) {
      switch (status) {
        case MaintenanceRequestStatus.New: {
          return this.$t("MaintenanceList.New");
        }
        case MaintenanceRequestStatus.InProgress: {
          return this.$t("MaintenanceList.InProgress");
        }
        case MaintenanceRequestStatus.Cancelled: {
          return this.$t("MaintenanceList.Cancelled");
        }
        case MaintenanceRequestStatus.Complete: {
          return this.$t("MaintenanceList.Completed");
        }
      }
    },
    openRequestEditDlg() {
      this.$refs.maintenanceRequestEditDlg.open(this.request);
    },
    openChecklist() {
      this.$refs.maintenanceChecklistDlg.open(this.request);
    },
    clearInputFile() {
      const fileInput = document.getElementById('fileInput') as HTMLInputElement;
      fileInput.value = "";
    },
    deleteForm(form) {
      this.$refs.confirmDlg.open(null, this.$t("MaintenanceSchedule.MaintenanceRequestFormDeleteMessage"), false).then(async response => {
        if (response && response.agree) {
          await this.$store.dispatch("deleteMaintenanceForm", form);

          await this.$store.dispatch("deleteMaintenanceFormFromRequestById", {
            requestId: this.request.id,
            formId: form.formId
          });

          this.$toast.open({
            type: "success",
            message: this.$t("MaintenanceView.FormDeleted")
          });

          await this.$store.dispatch('getMaintenanceRequest', this.$route.params.id);

          this.$forceUpdate();
        }
      })
    },
    editForm(form, status: string) {
        this.$refs.scheduledMaintenanceFormEditDlg.open(form, status);
    },
    completeForm(form) {
        this.$refs.scheduledMaintenanceFormCompleteDlg.open(form);
    },
    getQuestionValue(value) {
      if(typeof value === 'string') {
        if(value.includes("base64")) {
          return "True"
        }

        return value ? value : "-";
      }

      if(typeof value === 'object') {
        if(value?.type && ["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(value.type)) {
          return value.content ? "True" : "False";
        }

        if(Array.isArray(value)) {
          if(value.length) {
            return value.join(", ");
          } else {
            return "-";
          }
        }

        return "-"
      }

      if(typeof value === 'boolean') {
        return value ? "True" : "False";
      }

      if(!value) {
        return "-";
      }

      return value;
    },
    async onMaintenanceRequestChanged(){
      await this.$store.dispatch('getMaintenanceRequest', this.$route.params.id);
      this.setupRequestData()
    },
    reassignRequest() {
      this.isReassignRequest = !this.isReassignRequest;

      if(this.isReassignRequest) {
        this.$nextTick(() => {
          const element = document.getElementById('assignRequestSection');
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        });
      }
    }
  },
};
